/**
 * QR image utilities — compose, download, and share the payment QR.
 *
 * Extracted from PaymentPage.jsx so both the inline QR and the zoom modal
 * consume a single, well-tested implementation. All three helpers are pure
 * (no React dependencies) so they can run inside a Web Worker later if
 * canvas contention becomes an issue.
 */

/**
 * Compose a PNG blob from the QR image, optionally baking a caption
 * ("Pay ₹X to Merchant") below it via HTML5 Canvas. Returns a Promise
 * that resolves to a Blob, or null if compositing isn't possible.
 *
 * Shared by both downloadQr and shareQr so the same artwork is used
 * whether the customer downloads or hits "Share" (Web Share API).
 */
export function composeQrBlob(data) {
    const wantsCaption = !!data.qr_download_with_caption;
    return new Promise((resolve) => {
        // No-caption path: fetch the data URL and convert to blob directly.
        if (!wantsCaption || typeof document === "undefined" || typeof Image === "undefined") {
            try {
                fetch(data.qr_image).then((r) => r.blob()).then(resolve).catch(() => resolve(null));
            } catch { resolve(null); }
            return;
        }
        try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                try {
                    const qrSize = Math.max(img.naturalWidth, img.naturalHeight, 360);
                    const padding = 32;
                    const captionH = 96;
                    const canvasW = qrSize + padding * 2;
                    const canvasH = qrSize + padding * 2 + captionH;
                    const canvas = document.createElement("canvas");
                    canvas.width = canvasW;
                    canvas.height = canvasH;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) { resolve(null); return; }
                    // White background — many phone galleries display transparent
                    // PNGs as black, which destroys the QR. Always paint white.
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvasW, canvasH);
                    ctx.drawImage(img, padding, padding, qrSize, qrSize);
                    const merchant = (data.partner_name || data.snapshot?.display_name || "Merchant").slice(0, 32);
                    const amt = (() => {
                        try { return `₹${Number(data.amount).toLocaleString("en-IN")}`; }
                        catch { return `₹${data.amount}`; }
                    })();
                    ctx.fillStyle = "#0f172a";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    const captionY = canvasH - captionH / 2;
                    ctx.font = "bold 28px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
                    ctx.fillText(`Pay ${amt}`, canvasW / 2, captionY - 14);
                    ctx.font = "500 18px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
                    ctx.fillStyle = "#475569";
                    ctx.fillText(`to ${merchant}`, canvasW / 2, captionY + 18);
                    canvas.toBlob((blob) => resolve(blob || null), "image/png");
                } catch { resolve(null); }
            };
            img.onerror = () => resolve(null);
            img.src = data.qr_image;
        } catch { resolve(null); }
    });
}

/**
 * Trigger a download of the (optionally captioned) QR PNG. Uses
 * composeQrBlob to do the heavy lifting and falls back to a plain
 * data-URL anchor download if the blob compose fails.
 */
export async function downloadQr(data) {
    const filename = `upi-qr-${data.txn_id || "payment"}.png`;
    const blob = await composeQrBlob(data);
    if (blob) {
        const url = URL.createObjectURL(blob);
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } finally {
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        }
        return;
    }
    // Last-ditch fallback: anchor with raw data URL.
    try {
        const a = document.createElement("a");
        a.href = data.qr_image;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch { /* nothing more we can do */ }
}

/**
 * Share the QR via the OS-native Web Share API (iOS/Android share
 * sheet). Lets the customer paste it straight into WhatsApp / Telegram
 * / Signal etc. Falls back to the plain download path on browsers
 * without Web Share or without file-share support.
 *
 * Returns true when the native share sheet was actually opened, false
 * otherwise — caller can use that to nudge UI hints.
 */
export async function shareQr(data) {
    const filename = `upi-qr-${data.txn_id || "payment"}.png`;
    const merchant = data.partner_name || data.snapshot?.display_name || "Merchant";
    const amt = (() => {
        try { return `₹${Number(data.amount).toLocaleString("en-IN")}`; }
        catch { return `₹${data.amount}`; }
    })();
    const text = `Pay ${amt} to ${merchant}`;
    try {
        const blob = await composeQrBlob(data);
        if (blob && typeof navigator !== "undefined" && navigator.canShare) {
            const file = new File([blob], filename, { type: "image/png" });
            if (navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: "UPI QR", text });
                return true;
            }
        }
        if (typeof navigator !== "undefined" && navigator.share) {
            // Browsers without canShare (older Android) — try text-only share.
            await navigator.share({ title: "UPI QR", text });
            return true;
        }
    } catch {
        // User cancelled, or browser refused — silently fall through to download.
    }
    await downloadQr(data);
    return false;
}

/**
 * Pre-flight probe a merchant-supplied redirect URL to detect DNS
 * failure / connection refused BEFORE we send the customer there.
 *
 * Why: after a successful payment we auto-redirect the customer to the
 * partner's `success_redirect`. Partners sometimes configure placeholder
 * domains (e.g. `acme.example.com`) on their test links that don't
 * resolve. Without this probe the customer lands on a Chrome NXDOMAIN
 * page after a successful payment and panics that their money vanished.
 *
 * Implementation: `fetch(url, {method: "HEAD", mode: "no-cors"})` with
 * a 2.5-second hard timeout. The response is "opaque" — we can't read
 * it because of CORS — but any DNS failure / TCP refused / abort makes
 * the promise REJECT. That's the signal we care about. We treat:
 *   - resolve (any opaque response) → reachable
 *   - reject within 2.5s         → unreachable
 *   - timeout (2.5s elapses)    → treat as unreachable (slow merchant)
 *
 * Returns Promise<boolean>. Never throws.
 */
export async function probeRedirectUrl(url) {
    if (!url || typeof url !== "string") return false;
    try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 2500);
        await fetch(url, {
            method: "HEAD",
            mode: "no-cors",
            // `redirect: "follow"` so a 301/302 to a healthy host counts
            // as reachable — common pattern when the merchant uses an
            // alias domain that bounces to their canonical host.
            redirect: "follow",
            signal: ctrl.signal,
        });
        clearTimeout(timer);
        return true;
    } catch {
        return false;
    }
}
